import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Rocket, Plane, Cog, Mail, ShieldCheck } from "lucide-react";
import { useApp } from "@/AppContext";
import api, { formatApiErrorDetail } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AuthPage() {
  const { t, lang, toggleLang, setUser, refreshCart } = useApp();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", company: "", industry: "" });
  const [robot, setRobot] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register" && !robot) {
      setError(t("robot_required"));
      return;
    }
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload =
        mode === "login"
          ? { email: form.email, password: form.password }
          : {
              email: form.email,
              password: form.password,
              name: form.name,
              phone: form.phone,
              company: form.company,
              industry_class: form.industry,
            };
      const { data } = await api.post(path, payload);
      setUser(data);
      await refreshCart();
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const cats = [
    { icon: Rocket, label: t("drone") },
    { icon: Plane, label: t("aircraft") },
    { icon: Cog, label: t("machinery") },
  ];

  return (
    <div className="min-h-screen ps-grid-bg flex flex-col lg:flex-row relative" data-testid="auth-page">
      {/* Left hero */}
      <div className="lg:w-1/2 relative flex flex-col justify-center px-8 sm:px-16 py-16 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-cyan-500/10 blur-3xl" />
        <button
          data-testid="lang-toggle-btn"
          onClick={toggleLang}
          className="absolute top-6 left-6 lg:left-10 font-mono-ps text-sm border border-cyan-500/40 rounded-full px-4 py-1.5 text-cyan-700 hover:bg-cyan-500/10 transition"
        >
          {lang === "TR" ? "EN" : "TR"}
        </button>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="mb-6 flex items-center gap-3">
            <img src="/logo.png" alt="PartSpare" className="w-14 h-14 object-contain drop-shadow" data-testid="brand-logo" />
            <h1 className="font-display text-4xl font-black tracking-wider ps-neon-text">PARTSPARE</h1>
          </div>
          <h2 className="font-head text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-4">
            {t("tagline")}
          </h2>
          <p className="text-slate-500 text-lg max-w-md mb-10">{t("hero_sub")}</p>
          <div className="grid grid-cols-3 gap-4 max-w-md">
            {cats.map((c, i) => (
              <div key={i} className="ps-glow-card rounded-xl p-4 flex flex-col items-center gap-2 text-center">
                <c.icon className="text-cyan-600" size={30} />
                <span className="text-base text-slate-700 font-head font-semibold">{c.label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right form */}
      <div className="lg:w-1/2 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="ps-glow-card rounded-2xl w-full max-w-md p-8"
        >
          <div className="flex gap-2 mb-8 bg-slate-100 rounded-xl p-1">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 py-2.5 rounded-lg font-head font-semibold text-sm transition ${
                  mode === m ? "bg-cyan-500 text-slate-950" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {m === "login" ? t("login") : t("register")}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-slate-600">{t("email")}</Label>
              <Input
                data-testid="auth-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 bg-slate-50 border-slate-300"
                required
              />
            </div>
            <div>
              <Label className="text-slate-600">{t("password")}</Label>
              <Input
                data-testid="auth-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 bg-slate-50 border-slate-300"
                required
              />
            </div>

            {mode === "register" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-600">{t("name")}</Label>
                    <Input
                      data-testid="auth-name-input"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={t("name")}
                      className="mt-1.5 bg-slate-50 border-slate-300"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600">{t("phone_number")}</Label>
                    <Input
                      data-testid="auth-phone-input"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(123) 456-7890"
                      className="mt-1.5 bg-slate-50 border-slate-300"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600">{t("company")}</Label>
                  <Input
                    data-testid="auth-company-input"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder={t("company")}
                    className="mt-1.5 bg-slate-50 border-slate-300"
                  />
                </div>
                <div>
                  <Label className="text-slate-600">{t("industry_class")}</Label>
                  <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                    <SelectTrigger data-testid="auth-industry-select" className="mt-1.5 bg-slate-50 border-slate-300">
                      <SelectValue placeholder={t("select_industry")} />
                    </SelectTrigger>
                    <SelectContent>
                      {["Drone / İHA Yedek Parça", "Uçak Yedek Parça", "Makine Yedek Parça", "Diğer"].map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label data-testid="auth-recaptcha" className="flex items-center justify-between gap-3 border border-slate-300 rounded-md bg-slate-50 px-4 py-3 cursor-pointer select-none">
                  <span className="flex items-center gap-3">
                    <Checkbox checked={robot} onCheckedChange={(v) => setRobot(!!v)} data-testid="auth-robot-checkbox" />
                    <span className="text-sm text-slate-700">{t("not_a_robot")}</span>
                  </span>
                  <span className="flex flex-col items-center">
                    <ShieldCheck className="text-slate-400" size={22} />
                    <span className="text-[9px] text-slate-400 leading-tight">reCAPTCHA</span>
                  </span>
                </label>
              </>
            )}

            {error && (
              <p data-testid="auth-error" className="text-red-400 text-sm">{error}</p>
            )}

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-head font-bold text-base py-6"
            >
              {loading ? "..." : mode === "login" ? t("login") : t("register")}
            </Button>
          </form>

          {mode === "login" && (
            <Link
              data-testid="forgot-password-link"
              to="/forgot-password"
              className="block text-center mt-4 text-sm text-cyan-600 hover:underline"
            >
              {t("forgot_password")}
            </Link>
          )}
        </motion.div>
      </div>

      <div className="absolute bottom-4 left-0 right-0 flex justify-center px-4">
        <div className="flex items-center gap-2 text-slate-500 text-sm font-mono-ps">
          <Mail size={14} /> {t("contact_us")}
        </div>
      </div>
    </div>
  );
}
